import * as THREE from './three.module.min.js';
import { GLTFLoader } from './GLTFLoader.js';

const cutaways = document.querySelectorAll('[data-modelyard-cutaway]');
const desktopScrollQuery = window.matchMedia('(min-width: 990px)');
const stepOrder = ['intro', 'light', 'board', 'battery', 'cap'];
const materialRoles = {
  灯盖: 'headShell',
  灯头: 'headShell',
  灯板: 'light',
  主板: 'board',
  手柄: 'battery',
  底盖: 'cap',
};

const getScrollContainer = () => {
  if (desktopScrollQuery.matches) {
    return document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
  }

  return document.scrollingElement || document.documentElement;
};

const getViewportFrame = () => {
  const scrollContainer = getScrollContainer();

  if (scrollContainer.classList?.contains('page-wrapper')) {
    const rect = scrollContainer.getBoundingClientRect();

    return {
      top: rect.top,
      height: scrollContainer.clientHeight || rect.height || window.innerHeight,
    };
  }

  return {
    top: 0,
    height: window.innerHeight,
  };
};

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const smooth = (edge0, edge1, value) => {
  const x = clamp((value - edge0) / (edge1 - edge0));
  return x * x * (3 - 2 * x);
};

const lerp = (a, b, t) => a + (b - a) * t;
const getMaterials = (material) => (Array.isArray(material) ? material : [material]);
const ledPackagePattern = /(LED-ARRAY|5050|SMD_6P|S6-5050)/i;
const ledYellow = 0xffd21a;
const ledGlow = 0xffb400;

const sample = (keyframes, progress) => {
  for (let i = 0; i < keyframes.length - 1; i++) {
    const from = keyframes[i];
    const to = keyframes[i + 1];

    if (progress <= to.t) {
      const t = smooth(from.t, to.t, progress);

      return {
        x: lerp(from.x, to.x, t),
        y: lerp(from.y, to.y, t),
        view: lerp(from.view, to.view, t),
      };
    }
  }

  return keyframes[keyframes.length - 1];
};

cutaways.forEach((cutaway) => {
  const canvas = cutaway.querySelector('.modelyard-cutaway__canvas');
  const chapters = Array.from(cutaway.querySelectorAll('[data-cutaway-step]'));
  const indexNode = cutaway.querySelector('[data-cutaway-index]');
  const labelNode = cutaway.querySelector('[data-cutaway-label]');

  if (!canvas || !chapters.length) return;

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.18;

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
  camera.position.set(0, 0, 9);

  const root = new THREE.Group();
  root.rotation.x = -Math.PI / 2;
  scene.add(root);

  const keyLight = new THREE.DirectionalLight(0xfff2de, 4.3);
  keyLight.position.set(2.6, 3.4, 6);
  scene.add(keyLight);
  scene.add(new THREE.HemisphereLight(0xddeaff, 0x111313, 2.25));

  const rimLight = new THREE.DirectionalLight(0xbfd8ff, 1.8);
  rimLight.position.set(-3, 1, 4);
  scene.add(rimLight);

  const loadedParts = new Map();
  const reusableVector = new THREE.Vector3();
  let activeStep = 'intro';
  let modelReady = false;
  let modelHeight = 4.4;
  let modelWidth = 1.8;
  let viewHeight = 4.4;
  let lampCover = null;
  let lampCoverZ = 0;
  let headShell = null;
  let headShellZ = 0;
  let handleShell = null;
  let handleShellZ = 0;
  let tailCap = null;
  let tailCapZ = 0;
  let lampBoard = null;
  let lampBoardPosition = null;
  let lampBoardRotationX = 0;
  let lampBoardRotationZ = 0;
  let mainBoard = null;
  let mainBoardPosition = null;
  let mainBoardRotation = null;
  let mainBoardFocus = null;
  let batteryCell = null;
  let cameraKeyframes = [];
  let cameraReady = false;
  let renderInvalidated = true;
  let canvasWidth = 0;
  let canvasHeight = 0;
  const currentFrame = { x: 0, y: 0, view: 4.4 };
  const targetFrame = { x: 0, y: 0, view: 4.4 };

  const projection = () => {
    const box = canvas.getBoundingClientRect();
    const width = Math.max(1, box.width);
    const height = Math.max(1, box.height);
    const aspect = width / height;
    const safeViewHeight = Math.max(viewHeight, modelWidth / Math.max(0.6, aspect) / 0.78);
    const viewWidth = safeViewHeight * aspect;

    if (width !== canvasWidth || height !== canvasHeight) {
      canvasWidth = width;
      canvasHeight = height;
      renderer.setSize(width, height, false);
    }

    camera.left = -viewWidth / 2;
    camera.right = viewWidth / 2;
    camera.top = safeViewHeight / 2;
    camera.bottom = -safeViewHeight / 2;
    camera.updateProjectionMatrix();
  };

  const getScreenPoint = (name, fallback = new THREE.Vector3()) => {
    const object = loadedParts.get(name);

    if (!object) return fallback.clone();

    root.updateMatrixWorld(true);
    object.getWorldPosition(reusableVector);

    return reusableVector.clone();
  };

  const prepareMeshMaterials = (object) => {
    object.traverse((child) => {
      if (!child.isMesh) return;

      child.material = Array.isArray(child.material)
        ? child.material.map((material) => material.clone())
        : child.material.clone();

      const materials = getMaterials(child.material);
      child.userData.originalColors = materials.map((material) => material.color?.clone() || null);
      child.userData.originalEmissives = materials.map((material) => material.emissive?.clone() || null);
      child.userData.originalEmissiveIntensities = materials.map((material) => material.emissiveIntensity || 0);
    });
  };

  const applyMainBoardPalette = (object) => {
    const boardColor = new THREE.Color(0x1c342c);
    const ceramicColor = new THREE.Color(0xc9c2b0);
    const metalColor = new THREE.Color(0x9da59a);
    const chipColor = new THREE.Color(0x111314);
    const copperColor = new THREE.Color(0xb47a42);
    const accentColor = new THREE.Color(0x9b6961);

    object.traverse((child) => {
      if (!child.isMesh) return;

      child.geometry.computeBoundingBox();
      const size = child.geometry.boundingBox.getSize(new THREE.Vector3());
      const footprint = Math.max(size.x * size.y, size.x * size.z, size.y * size.z);
      const longest = Math.max(size.x, size.y, size.z);
      const shortest = Math.min(size.x, size.y, size.z);
      const materials = getMaterials(child.material);

      materials.forEach((material) => {
        const color = material.color || new THREE.Color(0xffffff);
        const brightness = (color.r + color.g + color.b) / 3;
        const target = new THREE.Color();

        if (footprint > 0.62 && longest > shortest * 8) {
          target.copy(boardColor);
        } else if (color.r > color.g * 1.18 && color.r > color.b * 1.18) {
          target.copy(accentColor);
        } else if (brightness > 0.72) {
          target.copy(ceramicColor);
        } else if (brightness < 0.22) {
          target.copy(chipColor);
        } else if (color.r > 0.48 && color.g > 0.34 && color.b < 0.24) {
          target.copy(copperColor);
        } else {
          target.copy(metalColor);
        }

        if (material.color) material.color.copy(target);
        if (material.emissive) {
          material.emissive.set(0x111210);
          material.emissiveIntensity = 0.02;
        }
        material.roughness = brightness > 0.72 ? 0.46 : 0.54;
        material.metalness = brightness > 0.72 ? 0.1 : 0.04;
        material.needsUpdate = true;
      });
    });
  };

  const createBatteryCell = () => {
    const group = new THREE.Group();
    const width = 0.68;
    const depth = 0.15;
    const length = 1.3;
    const cellMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x2e3633,
      roughness: 0.34,
      metalness: 0.02,
      clearcoat: 0.38,
      clearcoatRoughness: 0.26,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const metalMaterial = new THREE.MeshStandardMaterial({
      color: 0xb0aa9a,
      roughness: 0.22,
      metalness: 0.52,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const positiveMaterial = new THREE.MeshStandardMaterial({
      color: 0xc0a04c,
      roughness: 0.22,
      metalness: 0.48,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const trayMaterial = new THREE.MeshStandardMaterial({
      color: 0x111615,
      roughness: 0.5,
      metalness: 0.02,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });

    const tray = new THREE.Mesh(new THREE.BoxGeometry(width + 0.06, depth * 0.42, length + 0.06), trayMaterial);
    tray.position.y = depth * 0.18;
    group.add(tray);

    const cellWidth = width * 0.29;
    [-cellWidth * 1.08, 0, cellWidth * 1.08].forEach((x) => {
      const cell = new THREE.Mesh(new THREE.BoxGeometry(cellWidth, depth, length), cellMaterial.clone());
      cell.position.x = x;
      group.add(cell);
    });

    const topBus = new THREE.Mesh(new THREE.BoxGeometry(width * 0.82, depth * 0.16, 0.055), positiveMaterial);
    topBus.position.set(0, -depth * 0.5 - 0.014, length * 0.5 + 0.022);
    group.add(topBus);

    const lowerBus = new THREE.Mesh(new THREE.BoxGeometry(width * 0.82, depth * 0.14, 0.04), metalMaterial.clone());
    lowerBus.position.set(0, -depth * 0.5 - 0.012, -length * 0.5 - 0.012);
    group.add(lowerBus);

    const leftRail = new THREE.Mesh(new THREE.BoxGeometry(0.028, depth * 0.2, length * 0.92), metalMaterial.clone());
    leftRail.position.set(-width * 0.5 - 0.012, -depth * 0.5 - 0.004, 0);
    group.add(leftRail);

    const rightRail = leftRail.clone();
    rightRail.position.x = width * 0.5 + 0.012;
    group.add(rightRail);

    [-cellWidth * 0.54, cellWidth * 0.54].forEach((x) => {
      const divider = new THREE.Mesh(new THREE.BoxGeometry(0.018, depth * 0.18, length * 0.96), metalMaterial.clone());
      divider.position.set(x, -depth * 0.5 - 0.006, 0);
      group.add(divider);
    });

    group.traverse((child) => {
      if (!child.isMesh) return;

      child.userData.skipMaterialState = true;
    });

    return group;
  };

  const applyLightBoardMaterials = (object) => {
    const lensGeometry = new THREE.SphereGeometry(0.5, 24, 12);
    const lensMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xfff6c6,
      emissive: 0xffb31a,
      emissiveIntensity: 0.06,
      roughness: 0.04,
      metalness: 0,
      clearcoat: 1,
      clearcoatRoughness: 0.02,
      transmission: 0.72,
      thickness: 0.38,
      ior: 1.48,
      attenuationColor: new THREE.Color(0xffd36a),
      attenuationDistance: 1.8,
      reflectivity: 0.9,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.32,
      depthWrite: false,
    });

    object.traverse((child) => {
      if (!child.isMesh || child.userData.isLedLens || !ledPackagePattern.test(child.name || '')) return;

      child.geometry.computeBoundingBox();
      const bounds = child.geometry.boundingBox;
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());
      const lens = new THREE.Mesh(lensGeometry, lensMaterial.clone());

      lens.name = `${child.name}-lens`;
      lens.userData.isLedLens = true;
      lens.position.set(center.x, center.y, bounds.max.z + size.z * 0.05);
      lens.scale.set(size.x * 0.44, size.y * 0.44, size.z * 0.3);
      child.add(lens);

      child.userData.isLedPackage = true;
      child.material = new THREE.MeshPhysicalMaterial({
        color: ledYellow,
        emissive: ledGlow,
        emissiveIntensity: 0.42,
        roughness: 0.16,
        metalness: 0,
        clearcoat: 1,
        clearcoatRoughness: 0.06,
        reflectivity: 0.72,
        transparent: true,
        opacity: 0.92,
      });
    });
  };

  const createLedOpticMaterial = () =>
    new THREE.MeshPhysicalMaterial({
      color: ledYellow,
      emissive: ledGlow,
      emissiveIntensity: 0.34,
      roughness: 0.05,
      metalness: 0,
      clearcoat: 1,
      clearcoatRoughness: 0.02,
      transmission: 0.58,
      thickness: 0.42,
      ior: 1.48,
      attenuationColor: new THREE.Color(0xffd36a),
      attenuationDistance: 1.8,
      reflectivity: 0.88,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.62,
      depthWrite: false,
    });

  const replaceLightBoardLeds = (detail, ledModel) => {
    let prototypeMesh = null;
    const ledMeshes = [];

    ledModel.traverse((child) => {
      if (!prototypeMesh && child.isMesh) prototypeMesh = child;
    });

    detail.traverse((child) => {
      if (child.isMesh && ledPackagePattern.test(child.name || '')) ledMeshes.push(child);
    });

    if (!prototypeMesh || !ledMeshes.length) {
      applyLightBoardMaterials(detail);
      return;
    }

    prototypeMesh.geometry.computeBoundingBox();
    const prototypeBounds = prototypeMesh.geometry.boundingBox;
    const prototypeSize = prototypeBounds.getSize(new THREE.Vector3());
    const ledInstances = new THREE.InstancedMesh(prototypeMesh.geometry.clone(), createLedOpticMaterial(), ledMeshes.length);
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const rotation = new THREE.Quaternion();

    ledInstances.name = 'modelyard-led-5050-instances';
    ledInstances.userData.isLedPackage = true;

    ledMeshes.forEach((child, index) => {
      child.geometry.computeBoundingBox();
      const bounds = child.geometry.boundingBox;
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());

      scale.set(
        size.x / Math.max(0.001, prototypeSize.x),
        size.y / Math.max(0.001, prototypeSize.y),
        size.z / Math.max(0.001, prototypeSize.z)
      );
      position.set(center.x, center.y, bounds.max.z - prototypeBounds.min.z * scale.z + size.z * 0.05);
      matrix.compose(position, rotation, scale);
      ledInstances.setMatrixAt(index, matrix);
      child.visible = false;
    });

    ledInstances.instanceMatrix.needsUpdate = true;
    detail.add(ledInstances);
  };

  const fitDetailModel = (detail, target, parent) => {
    const wrapper = new THREE.Group();
    const localBox = new THREE.Box3().setFromObject(detail);
    const localCenter = localBox.getCenter(new THREE.Vector3());
    const localSize = localBox.getSize(new THREE.Vector3());

    detail.position.sub(localCenter);
    wrapper.add(detail);
    parent.add(wrapper);
    parent.updateMatrixWorld(true);
    wrapper.updateMatrixWorld(true);
    target.updateMatrixWorld(true);

    const targetBox = new THREE.Box3().setFromObject(target);
    const targetSize = targetBox.getSize(new THREE.Vector3());
    const targetCenter = targetBox.getCenter(new THREE.Vector3());
    parent.worldToLocal(targetCenter);

    const scale = Math.min(
      targetSize.x / Math.max(0.001, localSize.x),
      Math.max(targetSize.y, targetSize.z) / Math.max(0.001, localSize.y)
    );

    wrapper.scale.setScalar(scale);
    wrapper.position.copy(targetCenter);
    wrapper.visible = false;

    return wrapper;
  };

  const loadDetailModel = (url, target, parent) =>
    new Promise((resolve) => {
      if (!url || !target) {
        resolve(null);
        return;
      }

      loader.load(
        url,
        (detailGltf) => {
          const detail = detailGltf.scene;
          const finish = () => {
            if (url === cutaway.dataset.mainPcbUrl) applyMainBoardPalette(detail);
            prepareMeshMaterials(detail);
            const fitted = fitDetailModel(detail, target, parent);
            target.visible = false;
            resolve(fitted);
          };

          if (url === cutaway.dataset.lightPcbUrl && cutaway.dataset.ledUrl) {
            loader.load(
              cutaway.dataset.ledUrl,
              (ledGltf) => {
                replaceLightBoardLeds(detail, ledGltf.scene);
                finish();
              },
              undefined,
              () => {
                applyLightBoardMaterials(detail);
                finish();
              }
            );
            return;
          }

          finish();
        },
        undefined,
        () => resolve(null)
      );
    });

  const setActiveStep = (step) => {
    const stepIndex = stepOrder.indexOf(step);
    const chapter = chapters.find((item) => item.dataset.cutawayStep === step);

    activeStep = step;
    cutaway.dataset.activeStep = step;

    if (indexNode) indexNode.textContent = stepIndex <= 0 ? '00' : String(stepIndex).padStart(2, '0');
    if (labelNode && chapter) labelNode.textContent = chapter.dataset.cutawayLabel || chapter.querySelector('h2')?.textContent || '';
  };

  const stepFromProgress = (progress) => {
    if (progress < 0.22) return 'intro';
    if (progress < 0.48) return 'light';
    if (progress < 0.66) return 'board';
    if (progress < 0.86) return 'battery';
    return 'cap';
  };

  const weightsForProgress = (progress) => {
    const light = smooth(0.18, 0.42, progress) * (1 - smooth(0.5, 0.6, progress));
    const board = smooth(0.48, 0.64, progress) * (1 - smooth(0.68, 0.76, progress));
    const battery = smooth(0.66, 0.82, progress) * (1 - smooth(0.88, 0.94, progress));
    const cap = smooth(0.86, 0.98, progress);
    const headShell = Math.max(light * 0.35, board * 0.52);

    return { light, board, battery, cap, headShell };
  };

  const setMaterialState = (progress) => {
    if (!modelReady) return;

    const weights = weightsForProgress(progress);
    const hasFocus = Math.max(weights.light, weights.board, weights.battery, weights.cap) > 0.05;

    for (const [name, object] of loadedParts) {
      const role = materialRoles[name] || 'body';
      const roleWeight = weights[role] || 0;
      let opacity = hasFocus
        ? clamp(0.16 + roleWeight * 0.84 + (role === 'headShell' ? weights.board * 0.18 : 0), 0.1, 1)
        : 0.94;

      if (role === 'board') opacity = clamp(0.42 + roleWeight * 0.58, 0, 1);
      if (role === 'battery') opacity = hasFocus ? clamp(0.78 - weights.battery * 0.62 + weights.cap * 0.12, 0.16, 0.94) : 0.94;
      if (role === 'headShell' && weights.board > 0.04) opacity = clamp(0.035 + weights.light * 0.18 + weights.board * 0.035, 0.035, 0.24);
      if (role === 'light' || role === 'board') object.visible = activeStep === role || roleWeight > 0.02;

      object.traverse((child) => {
        if (!child.isMesh) return;

        const materials = getMaterials(child.material);

        materials.forEach((material, index) => {
          const originalColor = child.userData.originalColors?.[index];
          const originalEmissive = child.userData.originalEmissives?.[index];
          const originalEmissiveIntensity = child.userData.originalEmissiveIntensities?.[index] || 0;
          const isLedPackage = child.userData.isLedPackage;
          const isLedLens = child.userData.isLedLens;

          material.transparent = true;
          material.opacity = isLedPackage
            ? clamp(0.72 + roleWeight * 0.24, 0.72, 0.96)
            : isLedLens
              ? clamp(0.22 + roleWeight * 0.16, 0.22, 0.38)
              : opacity;
          material.depthWrite = !isLedPackage && !isLedLens && opacity > 0.5;

          if (originalColor && material.color) {
            material.color.copy(originalColor);
            if (isLedPackage) {
              material.color.set(ledYellow);
            } else if (isLedLens) {
              material.color.set(0xfff6c6);
            } else if (roleWeight > 0.08 && role !== 'light' && role !== 'board') {
              material.color.lerp(new THREE.Color(0xf8f0d2), roleWeight * 0.2);
            }
          }

          if (material.emissive) {
            if (originalEmissive) material.emissive.copy(originalEmissive);
            material.emissiveIntensity = originalEmissiveIntensity;

            if (isLedPackage) {
              material.emissive.set(ledGlow);
              material.emissiveIntensity = 0.24 + roleWeight * 0.74;
            } else if (isLedLens) {
              material.emissive.set(0xffb31a);
              material.emissiveIntensity = 0.04 + roleWeight * 0.14;
            } else if (roleWeight > 0.08 && role !== 'board') {
              material.emissive.lerp(new THREE.Color(0x3a2a12), roleWeight * 0.65);
              material.emissiveIntensity = 0.08 + roleWeight * 0.18;
            }
          }

          material.needsUpdate = true;
        });
      });
    }
  };

  const setTailCapState = (progress) => {
    if (!tailCap) return;

    const open = smooth(0.54, 0.68, progress) * (1 - smooth(0.8, 0.94, progress));
    tailCap.position.z = tailCapZ - open * 0.42;
  };

  const setAssemblySplitState = (progress) => {
    const lightSplit = smooth(0.18, 0.42, progress) * (1 - smooth(0.5, 0.62, progress));
    const boardReveal = smooth(0.48, 0.64, progress) * (1 - smooth(0.68, 0.78, progress));

    if (lampCover) lampCover.position.z = lampCoverZ + lightSplit * 0.42;
    if (headShell) headShell.position.z = headShellZ - lightSplit * 0.24 + boardReveal * 0.06;
    if (handleShell) handleShell.position.z = handleShellZ - lightSplit * 0.1;
  };

  const setLampBoardState = (progress) => {
    if (!lampBoard) return;

    const flip = smooth(0.2, 0.42, progress) * (1 - smooth(0.5, 0.62, progress));
    const tilt = smooth(0.24, 0.4, progress) * (1 - smooth(0.5, 0.62, progress));
    if (lampBoardPosition) {
      lampBoard.position.y = lampBoardPosition.y - flip * 0.28;
      lampBoard.position.z = lampBoardPosition.z + flip * 0.12;
    }
    lampBoard.rotation.x = lampBoardRotationX + tilt * 0.1;
    lampBoard.rotation.z = lampBoardRotationZ;
  };

  const setMainBoardState = (progress) => {
    if (!mainBoard || !mainBoardPosition || !mainBoardRotation) return;

    mainBoard.position.x = mainBoardPosition.x;
    mainBoard.position.y = mainBoardPosition.y;
    mainBoard.position.z = mainBoardPosition.z;
    mainBoard.rotation.x = mainBoardRotation.x;
    mainBoard.rotation.y = mainBoardRotation.y;
    mainBoard.rotation.z = mainBoardRotation.z;
  };

  const setBatteryState = (progress) => {
    if (!batteryCell) return;

    const weights = weightsForProgress(progress);
    batteryCell.visible = weights.battery > 0.02 || weights.cap > 0.02;
    batteryCell.traverse((child) => {
      if (!child.isMesh) return;

      child.material.opacity = clamp(weights.battery * 1.1 + weights.cap * 0.4, 0, 1);
      child.material.depthWrite = child.material.opacity > 0.5;
    });
  };

  const updateCamera = (progress) => {
    if (!cameraKeyframes.length) return;

    const sampled = sample(cameraKeyframes, progress);
    targetFrame.x = sampled.x;
    targetFrame.y = sampled.y;
    targetFrame.view = sampled.view;

    if (!cameraReady) {
      currentFrame.x = targetFrame.x;
      currentFrame.y = targetFrame.y;
      currentFrame.view = targetFrame.view;
      cameraReady = true;
    }
  };

  const renderFrame = () => {
    const dx = targetFrame.x - currentFrame.x;
    const dy = targetFrame.y - currentFrame.y;
    const dv = targetFrame.view - currentFrame.view;
    const moving = Math.abs(dx) + Math.abs(dy) + Math.abs(dv) > 0.001;

    if (!moving && !renderInvalidated) return;

    currentFrame.x += dx * 0.14;
    currentFrame.y += dy * 0.14;
    currentFrame.view += dv * 0.14;
    viewHeight = currentFrame.view;
    camera.position.x = currentFrame.x;
    camera.position.y = currentFrame.y;
    camera.lookAt(currentFrame.x, currentFrame.y, 0);
    projection();
    renderer.render(scene, camera);
    renderInvalidated = moving;
  };

  const updateCutaway = () => {
    const viewport = getViewportFrame();
    const sectionRect = cutaway.getBoundingClientRect();
    const progress = clamp(
      (viewport.top + viewport.height - sectionRect.top) / Math.max(1, sectionRect.height - viewport.height)
    );
    const nextStep = stepFromProgress(progress);

    cutaway.style.setProperty('--cutaway-progress', progress.toFixed(3));

    if (nextStep !== activeStep) setActiveStep(nextStep);

    setAssemblySplitState(progress);
    setTailCapState(progress);
    setLampBoardState(progress);
    setMainBoardState(progress);
    setBatteryState(progress);
    setMaterialState(progress);
    updateCamera(progress);
    renderInvalidated = true;
  };

  const loader = new GLTFLoader();

  loader.load(cutaway.dataset.modelUrl, (gltf) => {
    root.add(gltf.scene);

    const sourceBox = new THREE.Box3().setFromObject(gltf.scene);
    const sourceCenter = sourceBox.getCenter(new THREE.Vector3());
    gltf.scene.position.sub(sourceCenter);

    const board = gltf.scene.getObjectByName('主板');
    if (board) {
      board.position.z = -0.98;
      board.position.y = 0.02;
      board.rotation.x = Math.PI * 0.5;
      board.rotation.z = Math.PI * 0.5;
    }

    batteryCell = createBatteryCell();
    batteryCell.position.set(0, 0, -2.28);
    batteryCell.visible = false;
    gltf.scene.add(batteryCell);

    if (batteryCell) {
      batteryCell.traverse((child) => {
        if (!child.isMesh) return;

        child.userData.skipMaterialState = true;
      });
    }

    prepareMeshMaterials(gltf.scene);

    for (const name of Object.keys(materialRoles)) {
      const object = gltf.scene.getObjectByName(name);

      if (object) loadedParts.set(name, object);
    }

    Promise.all([
      loadDetailModel(cutaway.dataset.lightPcbUrl, loadedParts.get('灯板'), gltf.scene).then((detail) => {
        if (detail) loadedParts.set('灯板', detail);
      }),
      loadDetailModel(cutaway.dataset.mainPcbUrl, loadedParts.get('主板'), gltf.scene).then((detail) => {
        if (detail) loadedParts.set('主板', detail);
      }),
    ]).finally(() => {
      lampCover = loadedParts.get('灯盖') || null;
      lampCoverZ = lampCover?.position.z || 0;
      headShell = loadedParts.get('灯头') || null;
      headShellZ = headShell?.position.z || 0;
      handleShell = loadedParts.get('手柄') || null;
      handleShellZ = handleShell?.position.z || 0;
      tailCap = loadedParts.get('底盖') || null;
      tailCapZ = tailCap?.position.z || 0;
      lampBoard = loadedParts.get('灯板') || null;
      if (lampBoard) {
        lampBoard.position.x = 0;
        lampBoard.position.y = 0;
        lampBoard.position.z = -0.22;
        lampBoard.rotation.set(0, 0, 0);
      }
      lampBoardPosition = lampBoard?.position.clone() || null;
      lampBoardRotationX = lampBoard?.rotation.x || 0;
      lampBoardRotationZ = lampBoard?.rotation.z || 0;
      mainBoard = loadedParts.get('主板') || null;
      if (mainBoard) {
        const headCenter = new THREE.Box3().setFromObject(headShell || mainBoard).getCenter(new THREE.Vector3());
        gltf.scene.worldToLocal(headCenter);
        mainBoardFocus = new THREE.Object3D();
        mainBoardFocus.position.copy(headCenter);
        gltf.scene.add(mainBoardFocus);
        mainBoard.position.set(0, headCenter.y, headCenter.z);
        mainBoard.rotation.set(Math.PI * 0.5, 0, 0);
        mainBoard.scale.setScalar(0.018);
      }
      mainBoardPosition = mainBoard?.position.clone() || null;
      mainBoardRotation = mainBoard?.rotation.clone() || null;

      const fittedBox = new THREE.Box3().setFromObject(root);
      const fittedSize = fittedBox.getSize(new THREE.Vector3());
      modelHeight = Math.max(1, fittedSize.y);
      modelWidth = Math.max(1, fittedSize.x);
      viewHeight = modelHeight * (desktopScrollQuery.matches ? 1.16 : 1.34);

      root.updateMatrixWorld(true);
      const whole = fittedBox.getCenter(new THREE.Vector3());
      const light = getScreenPoint('灯板', whole);
      const boardPoint = mainBoardFocus ? mainBoardFocus.getWorldPosition(new THREE.Vector3()) : getScreenPoint('主板', light);
      const battery = getScreenPoint('手柄', whole);
      const cap = getScreenPoint('底盖', battery);

      cameraKeyframes = [
        { t: 0, x: whole.x, y: whole.y, view: modelHeight * (desktopScrollQuery.matches ? 1.16 : 1.34) },
        { t: 0.2, x: whole.x, y: whole.y, view: modelHeight * (desktopScrollQuery.matches ? 1.12 : 1.28) },
        { t: 0.44, x: light.x, y: light.y, view: 1.34 },
        { t: 0.64, x: boardPoint.x, y: boardPoint.y, view: 1.22 },
        { t: 0.84, x: battery.x, y: battery.y, view: 1.92 },
        { t: 1, x: cap.x, y: cap.y + 0.12, view: 1.5 },
      ];

      modelReady = true;
      setActiveStep('intro');
      updateCutaway();
      renderer.setAnimationLoop(renderFrame);
    });
  });

  const bindScroll = () => {
    document.removeEventListener('scroll', updateCutaway);
    window.removeEventListener('scroll', updateCutaway);
    document.querySelector('.page-wrapper')?.removeEventListener('scroll', updateCutaway);

    document.addEventListener('scroll', updateCutaway, { passive: true });
    window.addEventListener('scroll', updateCutaway, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', updateCutaway, { passive: true });
  };

  bindScroll();
  desktopScrollQuery.addEventListener('change', () => {
    bindScroll();
    renderInvalidated = true;
    updateCutaway();
  });
  window.addEventListener('resize', () => {
    canvasWidth = 0;
    canvasHeight = 0;
    renderInvalidated = true;
    updateCutaway();
  });

  setActiveStep('intro');
  projection();
  updateCutaway();
});
